import { useState, useEffect, useCallback } from 'react';
import { supabase, requireOrganizationId } from '../lib/supabase';
import { useToast } from '../components/ui/ToastModal';
import type { PnlSettings } from '../services/pnlCalculationService';
import { deriveConstructionIncomeExGst } from '../lib/claimFinancials';
import { logError } from "../lib/logError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface MonthlyIncome {
  month: number;
  year: number;
  income: number;
  retention: number;
}

interface MonthlyExpenseValue {
  id: string;
  category: string;
  monthDate: string; // "YYYY-MM-01"
  amount: number;
  source: 'manual' | 'xero';
}

interface UsePnlMonthlyDataReturn {
  /** Income & retention per month (auto-derived from claims_reports) */
  incomeByMonth: Map<string, MonthlyIncome>;
  /** Manual expense values per (category, month), the default source for P&L calc */
  expenseValues: MonthlyExpenseValue[];
  /** Xero-synced expense values per (category, month), for comparison */
  xeroExpenseValues: MonthlyExpenseValue[];
  /** Project financial settings */
  settings: PnlSettings;
  /** Loading state */
  loading: boolean;
  /** Update a single cell value (upsert), always writes as source='manual' */
  updateMonthlyValue: (category: string, monthDate: string, amount: number) => Promise<void>;
  /** Update project financial settings */
  updateSettings: (updates: Partial<PnlSettings>) => Promise<void>;
  /** Refresh all data */
  refreshData: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePnlMonthlyData(projectId: string): UsePnlMonthlyDataReturn {
  const { addToast } = useToast();
  const [incomeByMonth, setIncomeByMonth] = useState<Map<string, MonthlyIncome>>(new Map());
  const [expenseValues, setExpenseValues] = useState<MonthlyExpenseValue[]>([]);
  const [xeroExpenseValues, setXeroExpenseValues] = useState<MonthlyExpenseValue[]>([]);
  const [settings, setSettings] = useState<PnlSettings>({
    materialPercent: 30,
    wagesMultiplier: 1.04,
    estimateProfit: 0,
  });
  const [loading, setLoading] = useState(true);

  // ── Fetch all data ───────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      // 1. Fetch claims_reports → income + retention by month
      const { data: reports, error: reportsError } = await supabase
        .from('claims_reports')
        .select('claim_month, claim_year, subtotal, total_exc_gst, retention_amount, claim_this_month')
        .eq('project_id', projectId);

      if (reportsError) throw reportsError;

      const incomeMap = new Map<string, MonthlyIncome>();
      for (const r of reports || []) {
        const key = `${r.claim_year}-${String(r.claim_month).padStart(2, '0')}`;
        const grossIncome = deriveConstructionIncomeExGst({
          subtotal: Number(r.subtotal) || 0,
          totalExcGst: Number(r.total_exc_gst) || 0,
          retentionAmount: Number(r.retention_amount) || 0,
        });
        const retentionAmt = Number(r.retention_amount) || 0;
        const existing = incomeMap.get(key);
        if (existing) {
          existing.income += grossIncome;
          existing.retention += retentionAmt;
        } else {
          incomeMap.set(key, {
            month: r.claim_month,
            year: r.claim_year,
            income: grossIncome,
            retention: retentionAmt,
          });
        }
      }
      setIncomeByMonth(incomeMap);

      // 2. Fetch claims_pnl_monthly_values → all expense values (manual + xero)
      const { data: pnlValues, error: pnlError } = await supabase
        .from('claims_pnl_monthly_values')
        .select('*')
        .eq('project_id', projectId);

      if (pnlError) throw pnlError;

      const allValues = (pnlValues || []).map((v) => {
        const row = v as Record<string, unknown>;
        return {
          id: v.id,
          category: v.category,
          monthDate: v.month_date,
          amount: Number(v.amount) || 0,
          source: ((row.source as string) || 'manual') as 'manual' | 'xero',
        };
      });

      // Separate by source
      setExpenseValues(allValues.filter(v => v.source === 'manual'));
      setXeroExpenseValues(allValues.filter(v => v.source === 'xero'));

      // 3. Fetch project_financial_settings
      const { data: settingsData } = await supabase
        .from('project_financial_settings' as 'claims_projects')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();

      if (settingsData) {
        const sd = settingsData as Record<string, unknown>;
        const rawMat = Number(sd.target_material_percent);
        // DB stores as decimal fraction (e.g. 0.30 = 30%), convert to whole number for display
        const materialPct = rawMat > 0 ? (rawMat <= 1 ? rawMat * 100 : rawMat) : 30;
        setSettings({
          materialPercent: materialPct,
          wagesMultiplier: Number(sd.wages_multiplier) || 1.04,
          estimateProfit: Number(sd.estimate_profit) || 0,
        });
      }
    } catch (error: unknown) {
      logError('Error fetching P&L monthly data:', error);
      addToast({ type: 'error', title: 'Data Error', message: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  }, [projectId, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Upsert a single cell value ──────────────────────────────────────────
  const updateMonthlyValue = useCallback(
    async (category: string, monthDate: string, amount: number) => {
      try {
        // Check if record exists
        const existing = expenseValues.find(
          v => v.category === category && v.monthDate === monthDate
        );

        if (existing) {
          // Update
          const { error } = await supabase
            .from('claims_pnl_monthly_values')
            .update({ amount })
            .eq('id', existing.id);
          if (error) throw error;

          setExpenseValues(prev =>
            prev.map(v => (v.id === existing.id ? { ...v, amount } : v))
          );
        } else {
          // Insert (always as manual source)
          const { data, error } = await supabase
            .from('claims_pnl_monthly_values')
            .insert({
              organization_id: requireOrganizationId(),
              project_id: projectId,
              category,
              month_date: monthDate,
              amount,
              source: 'manual',
            })
            .select()
            .single();

          if (error) throw error;

          setExpenseValues(prev => [
            ...prev,
            {
              id: data.id,
              category: data.category,
              monthDate: data.month_date,
              amount: Number(data.amount) || 0,
              source: 'manual' as const,
            },
          ]);
        }

        addToast({ type: 'success', title: 'Saved', message: `${category} updated` });
      } catch (error: unknown) {
        logError('Error updating P&L value:', error);
        addToast({ type: 'error', title: 'Save Failed', message: error instanceof Error ? error.message : 'Unknown error' });
      }
    },
    [projectId, expenseValues, addToast]
  );

  // ── Update project financial settings ────────────────────────────────────
  const updateSettings = useCallback(
    async (updates: Partial<PnlSettings>) => {
      try {
        const dbUpdates: Record<string, number> = {};
        if (updates.materialPercent !== undefined) dbUpdates.target_material_percent = updates.materialPercent / 100;
        if (updates.wagesMultiplier !== undefined) dbUpdates.wages_multiplier = updates.wagesMultiplier;
        if (updates.estimateProfit !== undefined) dbUpdates.estimate_profit = updates.estimateProfit;

        // Upsert: try update first, then insert if not found
        const { data: existing } = await supabase
          .from('project_financial_settings' as 'claims_projects')
          .select('id')
          .eq('project_id', projectId)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from('project_financial_settings' as 'claims_projects')
            .update(dbUpdates as Record<string, unknown>)
            .eq('project_id', projectId) as { error: Error | null };
          if (error) throw error;
        } else {
          /* eslint-disable @typescript-eslint/no-explicit-any -- table not in generated Supabase types */
          const { error } = await (supabase as any)
            .from('project_financial_settings')
            .insert({ project_id: projectId, organization_id: requireOrganizationId(), ...dbUpdates });
          /* eslint-enable @typescript-eslint/no-explicit-any */
          if (error) throw error;
        }

        setSettings(prev => ({ ...prev, ...updates }));
        addToast({ type: 'success', title: 'Settings Saved', message: 'Financial settings updated' });
      } catch (error: unknown) {
        logError('Error updating settings:', error);
        addToast({ type: 'error', title: 'Save Failed', message: error instanceof Error ? error.message : 'Unknown error' });
      }
    },
    [projectId, addToast]
  );

  return {
    incomeByMonth,
    expenseValues,
    xeroExpenseValues,
    settings,
    loading,
    updateMonthlyValue,
    updateSettings,
    refreshData: fetchData,
  };
}
